import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { AppConfigService } from './app-config.service';

export interface CallOptions {
  redirectOnUnauthorized?: boolean;
  rethrowError?: boolean;
  emitRoleError?: boolean;
  emitGlobalError?: boolean;
  disableProgress?: boolean;
}

const defaultCallOptions: CallOptions = {
  redirectOnUnauthorized: true,
  rethrowError: false,
  emitRoleError: true,
  emitGlobalError: true,
  disableProgress: false
};

@Injectable()
export class HttpService {
  private static readonly requestOptions = {
    responseType: 'json' as 'json',
    headers: new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }),
    observe: 'response' as 'response'
  };

  apiEndpoint: string;

  loadEnd$: Observable<{}>;
  loadStart$: Observable<{}>;

  errors$: Observable<shared.ErrorResponse>;
  roleErrors$: Observable<{}>;
  authorizationErrors$: Observable<{}>;

  private loadEndStream = new Subject<{}>();
  private loadStartStream = new Subject<{}>();

  private errorStream = new Subject<shared.ErrorResponse>();
  private roleErrorStream = new Subject<{}>();
  private authorizationErrorStream = new Subject<{}>();

  constructor(private http: HttpClient, appConfig: AppConfigService) {
    this.apiEndpoint = appConfig.apiEndpoint;

    this.loadEnd$ = this.loadEndStream.asObservable();
    this.loadStart$ = this.loadStartStream.asObservable();

    this.errors$ = this.errorStream.asObservable();
    this.roleErrors$ = this.roleErrorStream.asObservable();
    this.authorizationErrors$ = this.authorizationErrorStream.asObservable();
  }

  get<T>(url: string, options?: CallOptions) {
    const request = this.http.get(this.getApiUrl(url), HttpService.requestOptions);
    return this.handleRequest<T>(request, options);
  }

  delete<T>(url: string, options?: CallOptions) {
    const request = this.http.delete(this.getApiUrl(url), HttpService.requestOptions);
    return this.handleRequest<T>(request, options);
  }

  put<T, B>(url: string, body: B, options?: CallOptions) {
    const request = this.http.put(
      this.getApiUrl(url),
      JSON.stringify(body),
      HttpService.requestOptions
    );
    return this.handleRequest<T>(request, options);
  }

  post<T, B>(url: string, body: B, options?: CallOptions) {
    const request = this.http.post(
      this.getApiUrl(url),
      JSON.stringify(body),
      HttpService.requestOptions
    );
    return this.handleRequest<T>(request, options);
  }

  uploadFile(url: string, file: File) {
    const formData = new FormData();
    formData.append('fileToUpload', file, file.name);

    return this.http
      .post(this.getApiUrl(url), formData, { observe: 'events', reportProgress: true })
      .filter(
        event =>
          event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response
      )
      .map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            return { progress: Math.round(event.loaded * 100 / (event.total || 1)) };
          case HttpEventType.Response:
            if (!event.ok) {
              throw new Error((event.body || {}).toString());
            }
            return { progress: 100 };
          default:
            throw new Error('Unknown HTTP event type');
        }
      }) as Observable<{ progress: number }>;
  }

  private handleRequest<T>(
    request: Observable<HttpResponse<Object>>,
    options?: CallOptions
  ): Observable<T> {
    const optionsOrDefault = { ...defaultCallOptions, ...options };

    if (!optionsOrDefault.disableProgress) {
      this.loadStartStream.next();
    }

    return request
      .map(response => {
        try {
          this.stopLoading(optionsOrDefault);
          return response.body as any;
        } catch (error) {
          return {};
        }
      })
      .catch(error => this.handleError(error, optionsOrDefault));
  }

  private handleError(response: HttpErrorResponse, options: CallOptions) {
    this.stopLoading(options);

    if (response.status === 401 && options.redirectOnUnauthorized) {
      this.authorizationErrorStream.next();
    } else if (response.status === 403 && options.emitRoleError) {
      this.roleErrorStream.next();
    }

    const error = this.extractError(response);
    if (options.emitGlobalError) {
      this.errorStream.next(error);
    }

    return options.rethrowError ? Observable.throw(error) : Observable.empty();
  }

  private stopLoading(options: CallOptions) {
    if (!options.disableProgress) {
      this.loadEndStream.next();
    }
  }

  private extractError(response: HttpErrorResponse) {
    try {
      return response.error.LocalizationKey ? response.error : this.defaultError;
    } catch (err) {
      return this.defaultError;
    }
  }

  private get defaultError() {
    return {
      ErrorCode: 'UnspecifiedByServer',
      LocalizationKey: 'Errors.serverFailedToExecute',
      LocalizationParams: [],
      Message: 'Failed to execute request on server.'
    } as shared.ErrorResponse;
  }

  private getApiUrl(url: string) {
    return `${this.apiEndpoint}/${url}`;
  }
}
